import { useState } from 'react';
import { ThemeButton } from './ThemeWrapper';
import { labFleetAPI } from '../services/api';

function errorMessage(err, fallback) {
  return err?.response?.data?.message || err?.message || fallback;
}

function LabFleetMerge({ account, disabled, onMerged }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mergingId, setMergingId] = useState('');
  const [error, setError] = useState('');

  const load = async (q = query) => {
    setLoading(true);
    setError('');
    try {
      const res = await labFleetAPI.similar({
        unixUsername: account.unixUsername,
        alias: account.alias,
        home: account.home,
        q,
      });
      setSuggestions(res.data?.suggestions || []);
    } catch (err) {
      setSuggestions([]);
      setError(errorMessage(err, 'Could not look up similar students'));
    } finally {
      setLoading(false);
    }
  };

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) await load('');
  };

  const merge = async (suggestion) => {
    const confirmed = window.confirm(
      `Link ${account.unixUsername} to ${suggestion.fullName} (${suggestion.studentId})? The SMS student keeps their profile. This Linux name is stored on that student.`
    );
    if (!confirmed) return;
    setMergingId(suggestion.studentId);
    setError('');
    try {
      const res = await labFleetAPI.merge({
        unixUsername: account.unixUsername,
        alias: account.alias,
        home: account.home,
        studentId: suggestion.studentId,
      });
      setOpen(false);
      if (onMerged) await onMerged(res.data?.message || `Linked ${account.unixUsername}.`);
    } catch (err) {
      setError(errorMessage(err, 'Could not merge this account'));
    } finally {
      setMergingId('');
    }
  };

  return (
    <div className="mt-2">
      <ThemeButton type="button" onClick={toggle} disabled={disabled || Boolean(mergingId)}>
        {open ? 'Hide similar students' : 'Merge with SMS'}
      </ThemeButton>
      {open ? (
        <div className="mt-2 space-y-2" style={{ minWidth: 260 }}>
          <p className="text-xs theme-text-secondary">
            Suggestions use the Linux name and the name on the server. Email is not required.
          </p>
          <form
            className="flex gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              load(query);
            }}
          >
            <input
              className="px-2 py-1 border rounded text-xs flex-1"
              placeholder="Search name, VE ID, or phone"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
            <ThemeButton type="submit" disabled={loading}>
              Search
            </ThemeButton>
          </form>
          {error ? (
            <p className="text-xs" style={{ color: '#b91c1c' }}>
              {error}
            </p>
          ) : null}
          {loading ? <p className="text-xs theme-text-secondary">Looking up students…</p> : null}
          {!loading && !suggestions.length ? (
            <p className="text-xs theme-text-secondary">No similar student. Search by name or VE ID.</p>
          ) : null}
          <ul className="space-y-2">
            {suggestions.map((suggestion) => (
              <li key={suggestion.studentId} className="text-xs" style={{ borderTop: '1px solid var(--color-border)', paddingTop: 6 }}>
                <div className="font-medium theme-text">{suggestion.fullName}</div>
                <div className="theme-text-secondary">
                  {suggestion.studentId}
                  {suggestion.courseName ? ` · ${suggestion.courseName}` : ''}
                  {suggestion.batchName ? ` · ${suggestion.batchName}` : ''}
                </div>
                <div className="theme-text-secondary">
                  {suggestion.email || 'No email'}
                  {suggestion.phone ? ` · ${suggestion.phone}` : ''}
                  {suggestion.inactive ? ' · Inactive' : ''}
                </div>
                <div className="theme-text-secondary">{suggestion.reasons.join(' ')}</div>
                {suggestion.canMerge ? (
                  <ThemeButton
                    type="button"
                    onClick={() => merge(suggestion)}
                    disabled={disabled || Boolean(mergingId)}
                  >
                    {mergingId === suggestion.studentId ? 'Linking…' : 'Link this student'}
                  </ThemeButton>
                ) : (
                  <div className="theme-text-secondary">{suggestion.blockReason}</div>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export default LabFleetMerge;
